import { FilterModel, AdvancedFilterModel, ColDef } from "ag-grid-community";
import { parseISO } from "date-fns";
import { TypeSimpleComponent, TypeSimpleComponentValue } from "enum";
import { RSQLCriteria } from "rsql-criteria-typescript";
import { ComponentReadOnly, ComponentState } from "types/Component";
import { FilterProperties } from "types/InteractiveReport";
import { DatatableSort } from "composants/datatable/Table";
import {
  convertTermByOperator,
  GS,
  GSBuilder,
  GSComparison,
  mapToRSQL,
  RSQLOperator,
} from "utils/query.utils";
import { AGFilter, Filtermodel } from "../Grid.types";
import * as api from "api/datatable";
import { getDatatableFocusModeAdmin } from "api/adminDatatable";
import { DatatableFocusState, FocusState } from "types/Focus";
import { AxiosPromise, CancelTokenSource } from "axios";
import { FilterBar } from "types/Search";
import { GroupByFunctionParameter } from "types/InteractiveReport";
import { PagedResource } from "types/Search";
import { Pojo } from "types/Galaxy";
import { uuidv4 } from "utils/uuid.utils";
import toaster from "composants/toaster/toaster";
import { NotificationGroup } from "@axin-org/comet";
import { t } from "utils/i18n";
import { MIN_BATCH_SIZE } from "../AgGridContainer";

/**
 * Fonction qui transforme les filtres AG Grid en RSQL
 * searchvalues est de la forme
 {
    "ldclDesignation": {
        "filterType": "text",
        "operator": "AND",
        "condition1": {
            "filterType": "text",
            "type": "contains",
            "filter": "defr"
        },
        "condition2": {
            "filterType": "text",
            "type": "contains",
            "filter": "sdf"
        },
        "conditions": [
            {
                "filterType": "text",
                "type": "contains",
                "filter": "defr"
            },
            {
                "filterType": "text",
                "type": "contains",
                "filter": "sdf"
            }
        ]
    },
    "articleId": {
        "filterType": "text",
        "type": "contains",
        "filter": "ppp"
    }
  }
 */
export function getRSQLFilter(
  interactions: Record<string, any>,
  searchValues: AGFilter,
  sortValues: Record<string, DatatableSort>,
  breakColumns: string[],
  additionnalClause: RSQLCriteria | undefined,
  columns: Record<string, ComponentState>,
): RSQLCriteria {
  const searchFields = Object.keys(searchValues);

  const criteriaList: GS[] = [];
  for (let field of searchFields) {
    const col = columns[field.split(".")[0]];
    if (!col) continue;

    if (searchValues[field]) {
      const searchValue = searchValues[field];
      let criteria: GS | undefined;

      if (searchValue.operator === "AND" || searchValue.operator === "OR") {
        let conditions: GS[] = [];
        // Cas avec plusieurs conditions

        if (col.typeCompo === TypeSimpleComponent.AUTO_COMPLETE) {
          conditions = (
            searchValue as {
              conditions: Filtermodel[];
            }
          ).conditions.map((condition) => {
            if (condition.type !== "blank") {
              return mapcriteriaForGS(col, condition, field);
            } else {
              return mapOneFilter(condition, field);
            }
          });
        } else {
          conditions = (
            searchValue as {
              conditions: Filtermodel[];
            }
          ).conditions.map((condition) => mapOneFilter(condition, field));
        }

        criteria =
          searchValue.operator === "AND"
            ? GSBuilder.AND(...conditions)
            : GSBuilder.OR(...conditions);
      } else {
        // Cas avec une seule condition
        if (col.typeCompo === TypeSimpleComponent.AUTO_COMPLETE && searchValue.type !== "blank") {
          criteria = mapcriteriaForGS(col, searchValue, field);
        } else {
          criteria = mapOneFilter(searchValue, field);
        }
      }
      criteriaList.push(criteria);
    }
  }
  // on transforme l'interaction en rsql
  const rsql = mapToRSQL(interactions, sortValues, breakColumns);

  // on ajoute les filters à notre rsql
  const filtered = GSBuilder.toFilter(GSBuilder.AND(...criteriaList));
  rsql.filters.and(filtered);

  if (additionnalClause) {
    rsql.and(additionnalClause);
  }

  return rsql;
}

export function getFilterAllField(
  definition: { columns: ComponentState[] },
  filterAllFields: string,
) {
  const filterAllFieldsModel = GSBuilder.OR(
    ...definition.columns
      .filter(
        (c) =>
          c.typeCompo !== TypeSimpleComponent.OEL &&
          c.typeCompo !== TypeSimpleComponent.LINK &&
          c.typeCompo !== TypeSimpleComponent.CHECKBOX &&
          c.typeCompo !== TypeSimpleComponent.STATIC_HTML &&
          c.typeCompo !== TypeSimpleComponent.LINK &&
          c.typeCompo !== TypeSimpleComponent.CALENDAR &&
          c.typeCompo !== TypeSimpleComponent.CALENDAR_HOUR &&
          c.typeCompo !== TypeSimpleComponent.CALENDAR_HOUR_SECOND &&
          c.compoVisible,
      )
      .map((column) => {
        if (column.typeCompo === TypeSimpleComponent.AUTO_COMPLETE) {
          return mapcriteriaForGS(
            column,
            {
              filter: filterAllFields,
              filterType: "text",
              type: "contains",
            },
            column.column,
          );
        }

        return mapOneFilter(
          {
            filter: filterAllFields,
            filterType: mapTypeCompoToFilterType(column.typeCompo as TypeSimpleComponentValue),
            type: "contains",
          },
          column.column,
        );
      }),
  );
  return GSBuilder.toFilter(filterAllFieldsModel);
}

export function explainFilter(
  filterModel: Record<string, FilterModel | AdvancedFilterModel | null>,
  allColumnRef: Record<string, ComponentState>,
  localeText: Record<string, string>,
  booleanLabels?: { yes: string; no: string },
) {
  const result: string[] = [];

  Object.keys(filterModel).forEach((key) => {
    const currentFilter = (filterModel as AGFilter)[key];

    if (currentFilter.conditions) {
      result.push(
        currentFilter.conditions
          .map((condition: Filtermodel) =>
            explainOneFilter(
              condition as FilterModel,
              allColumnRef[key],
              localeText,
              booleanLabels,
            ),
          )
          .join(
            ` ${
              localeText[
                currentFilter.operator === "AND" ? "advancedFilterAnd" : "advancedFilterOr"
              ]
            } `,
          ),
      );
    } else {
      result.push(
        explainOneFilter(
          currentFilter as FilterModel,
          allColumnRef[key],
          localeText,
          booleanLabels,
        ),
      );
    }
  });

  return result;
}

function explainOneFilter(
  filterModel: FilterModel,
  columnDef: ComponentState,
  localeText: Record<string, string>,
  booleanLabels?: { yes: string; no: string },
) {
  switch (filterModel.filterType) {
    case "set": {
      const operatorLabel = localeText["equals"];
      const orLabel = localeText["orCondition"];

      const isBooleanType =
        columnDef.typeCompo === TypeSimpleComponent.CHECKBOX ||
        columnDef.typeCompo === TypeSimpleComponent.BOOLEAN;

      if (isBooleanType && booleanLabels) {
        const boolMap: Record<string, string> = {
          O: booleanLabels.yes,
          N: booleanLabels.no,
        };
        return `${columnDef?.label} ${operatorLabel} ${(filterModel as any).values
          ?.map((v: string) => boolMap[v] ?? v)
          ?.join(` ${orLabel} `)}`;
      } else if (columnDef.sysDomaineChoices) {
        return `${columnDef?.label} ${operatorLabel} ${(filterModel as any).values
          ?.map((v: string) => columnDef.sysDomaineChoices?.find((d) => d.value === v)?.label)
          ?.join(` ${orLabel} `)}`;
      } else {
        return `${columnDef?.label} ${operatorLabel} ${(filterModel as any).values
          ?.map((v: string) => `"${v}"`)
          ?.join(` ${orLabel} `)}`;
      }
    }

    case "date": {
      const type = (() => {
        switch (filterModel.type) {
          case "lessThan":
            return "before";
          case "greaterThan":
            return "after";
          default:
            return filterModel.type;
        }
      })();
      const operatorLabel = localeText[type];

      const dateFrom = (filterModel as any).dateFrom
        ? formatdateALaRache((filterModel as any).dateFrom)
        : "";
      const dateTo = (filterModel as any).dateTo
        ? formatdateALaRache((filterModel as any).dateTo)
        : "";
      return `${columnDef?.label} ${operatorLabel} ${dateFrom} ${
        dateFrom && dateTo ? localeText["advancedFilterAnd"] : ""
      } ${dateTo}`;
    }

    case "number": {
      const operatorLabel = localeText[filterModel.type as string];
      const value = (filterModel as any).filter;
      return `${columnDef?.label} ${operatorLabel} ${value} ${
        value && (filterModel as any).filterTo ? localeText["advancedFilterAnd"] : ""
      }`;
    }

    default: {
      const value = (filterModel as any).filter;
      const operator = filterModel.type as string;
      return `${columnDef?.label} ${localeText[operator]} ${value ? `"${value}"` : ""}`;
    }
  }
}

function formatdateALaRache(date: string) {
  return date.replace("00:00:00", "").replaceAll("-", "/").replace("T", " ");
}

export function getGroupByFilter(
  groupByCols: ColDef[],
  groupKeys: any[],
  rsql: RSQLCriteria,
  originGroupKeys: any[],
) {
  const groupFilters = groupByCols.slice(0, groupKeys.length).map((col, index) => {
    if (typeof originGroupKeys[index] === "object") {
      return GSBuilder.Comparison(`${col.field}.id`, "OPER_EQ", (originGroupKeys[index] as any).id);
    }

    return GSBuilder.Comparison(col.field as string, "OPER_EQ", originGroupKeys[index]);
  });

  if (groupFilters.length > 0) {
    const groupFilter = GSBuilder.AND(...groupFilters);
    return GSBuilder.toFilter(groupFilter);
  }
  return undefined;
}

export function mapFiltersToAgGridFilterModel(
  filters: FilterProperties[],
  columns: ComponentState[],
): Record<string, FilterModel> {
  return filters.reduce(
    (acc, filter) => {
      const column = columns.find((c) => c.column === filter.column);
      if (column) {
        const typeCompo = convertTypeCompoColumnIr(column).typeCompo;
        const filterType = mapTypeCompoToFilterType(typeCompo as TypeSimpleComponentValue);
        const values =
          typeCompo === TypeSimpleComponent.SYS_DOMAINE_IR
            ? column.sysDomaineChoices?.map((v: any) => v.id)
            : typeCompo === TypeSimpleComponent.BOOLEAN ||
                typeCompo === TypeSimpleComponent.CHECKBOX
              ? [filter.value]
              : [];
        acc[filter.column] =
          filterType === "date"
            ? ({
                filterType,
                type: revertOperator(filter.operator),
                // AG Grid stocke les dates dans dateFrom/dateTo, pas dans filter.
                // On garde value en repli pour les anciens focus déjà sauvegardés.
                dateFrom: filter.dateFrom ?? filter.value,
                dateTo: filter.dateTo,
              } as FilterModel)
            : ({
                filter: filter.value,
                filterType,
                type: revertOperator(filter.operator),
                values,
              } as FilterModel);
        return acc;
      }
      return {};
    },
    {} as Record<string, FilterModel>,
  );
}

export function convertOperator(operator: string): RSQLOperator {
  switch (operator) {
    case "contains":
      return "OPER_LIKE_ANYWHERE";
    case "notContains":
      return "OPER_DOES_NOT_CONTAIN";
    case "startsWith":
      return "OPER_LIKE_START";
    case "endsWith":
      return "OPER_LIKE_END";
    case "equals":
      return "OPER_EQ";
    case "notEqual":
      return "OPER_NE";
    case "lessThan":
      return "OPER_LT";
    case "greaterThan":
      return "OPER_GT";
    case "lessThanOrEqual":
      return "OPER_LE";
    case "greaterThanOrEqual":
      return "OPER_GE";
    case "in":
      return "OPER_IN";
    case "notIn":
      return "OPER_NOT_IN";
    case "blank":
      return "OPER_NULL";
    case "notBlank":
      return "OPER_NOT_NULL";
  }
  return operator as RSQLOperator;
}

function revertOperator(operator: string): string {
  switch (operator) {
    case "OPER_LIKE_ANYWHERE":
      return "contains";
    case "OPER_DOES_NOT_CONTAIN":
      return "notContains";
    case "OPER_LIKE_START":
      return "startsWith";
    case "OPER_LIKE_END":
      return "endsWith";
    case "OPER_EQ":
      return "equals";
    case "OPER_NE":
      return "notEqual";
    case "OPER_LT":
      return "lessThan";
    case "OPER_GT":
      return "greaterThan";
    case "OPER_LE":
      return "lessThanOrEqual";
    case "OPER_GE":
      return "greaterThanOrEqual";
    case "OPER_IN":
      return "in";
    case "OPER_NOT_IN":
      return "notIn";
    case "OPER_NULL":
      return "blank";
    case "OPER_NOT_NULL":
      return "notBlank";
  }
  return operator;
}

export function mapOneFilter(condition: Filtermodel, field: string): GS {
  let value: string | Date = condition.filter;
  let operator: RSQLOperator = "OPER_EQ";
  if (condition.filterType === "text") {
    switch (condition.type) {
      case "contains":
        operator = "OPER_LIKE_ANYWHERE";
        value = convertTermByOperator(value, "OPER_LIKE_ANYWHERE");
        break;
      case "notContains":
        operator = "OPER_DOES_NOT_CONTAIN";
        value = convertTermByOperator(value, "OPER_DOES_NOT_CONTAIN");
        break;
      case "equals":
        operator = "OPER_EQ";
        break;
      case "notEqual":
        operator = "OPER_NE";
        break;
      case "startsWith":
        operator = "OPER_LIKE_START";
        value = convertTermByOperator(value, "OPER_LIKE_START");
        break;
      case "endsWith":
        operator = "OPER_LIKE_END";
        value = convertTermByOperator(value, "OPER_LIKE_END");
        break;
      case "blank":
        operator = "OPER_NULL";
        break;
      case "notBlank":
        operator = "OPER_NOT_NULL";
        break;
      default:
        operator = "OPER_EQ";
        break;
    }
  } else if (condition.filterType === "date") {
    switch (condition.type) {
      case "equals":
        operator = "OPER_EQ";
        if (condition.dateFrom) {
          value = parseISO(condition.dateFrom);
        }
        break;
      case "notEqual":
        operator = "OPER_NE";
        if (condition.dateFrom) {
          value = parseISO(condition.dateFrom);
        }
        break;
      case "lessThan":
        operator = "OPER_LT";
        if (condition.dateFrom) {
          value = parseISO(condition.dateFrom);
        }
        break;
      case "greaterThan":
        operator = "OPER_GT";
        if (condition.dateFrom) {
          value = parseISO(condition.dateFrom);
        }
        break;
      case "inRange":
        if (condition.dateTo && condition.dateFrom) {
          return GSBuilder.AND(
            GSBuilder.Comparison(field, "OPER_LT", parseISO(condition.dateTo)),
            GSBuilder.Comparison(field, "OPER_GT", parseISO(condition.dateFrom)),
          );
        }
        break;
      case "blank":
        operator = "OPER_NULL";
        break;
      case "notBlank":
        operator = "OPER_NOT_NULL";
        break;
      default:
        operator = "OPER_EQ";
    }
  } else if (condition.filterType === "number") {
    switch (condition.type) {
      case "equals":
        operator = "OPER_EQ";
        break;
      case "notEqual":
        operator = "OPER_NE";
        break;
      case "lessThan":
        operator = "OPER_LT";
        break;
      case "greaterThan":
        operator = "OPER_GT";
        break;
      case "lessThanOrEqual":
        operator = "OPER_LE";
        break;
      case "greaterThanOrEqual":
        operator = "OPER_GE";
        break;
      case "inRange":
        return GSBuilder.AND(
          GSBuilder.Comparison(field, "OPER_LE", condition.filterTo),
          GSBuilder.Comparison(field, "OPER_GE", condition.filter),
        );
      case "blank":
        operator = "OPER_NULL";
        break;
      case "notBlank":
        operator = "OPER_NOT_NULL";
        break;
      default:
        operator = "OPER_EQ";
    }
  } else if (condition.filterType === "set") {
    if (condition.values && condition.values.length > 0) {
      return GSBuilder.Comparison(field, "OPER_IN", condition.values);
    }
  }
  return GSBuilder.Comparison(field, operator, value);
}

function isNegativeFilter(condition: Filtermodel): boolean {
  return condition.type === "notEqual" || condition.type === "notContains";
}

export function mapcriteriaForGS(col: ComponentState, condition: Filtermodel, field: string): GS {
  const value = condition.filter;
  const invertLogicalOps = isNegativeFilter(condition);

  const correctedNode = GSBuilder.visit<GS>(col.additionalClause, {
    And: (node, visitor) => {
      let result: GS[] = [];
      let isValueFromUser = false;
      for (let n of node.nodes) {
        let visitedNode = GSBuilder.visit(n, visitor);
        if (visitedNode) result.push(visitedNode);
        if (
          visitedNode?.type === "GSCOMPARISON" &&
          visitedNode?.value !== undefined &&
          visitedNode?.value !== null &&
          visitedNode?.value !== ""
        ) {
          isValueFromUser = true;
        }
      }
      return invertLogicalOps && isValueFromUser
        ? GSBuilder.OR(...result)
        : GSBuilder.AND(...result);
    },
    Or: (node, visitor) => {
      let result: GS[] = [];
      let isValueFromUser = false;
      for (let n of node.nodes) {
        let visitedNode = GSBuilder.visit(n, visitor);
        if (visitedNode) result.push(visitedNode);
        if (
          visitedNode?.type === "GSCOMPARISON" &&
          visitedNode?.value !== undefined &&
          visitedNode?.value !== null &&
          visitedNode?.value !== ""
        ) {
          isValueFromUser = true;
        }
      }
      return invertLogicalOps && isValueFromUser
        ? GSBuilder.AND(...result)
        : GSBuilder.OR(...result);
    },
    Comparison: (param) => {
      const criteria = mapOneFilter(condition, `${field}.${param.field}`);
      const isValueEmpty = param.value === "" || param.value === undefined || param.value === null;
      return {
        ...criteria,
        field: `${field}.${param.field}`,
        operator: isValueEmpty ? (criteria as GSComparison).operator : param.operator,
        value: !isValueEmpty
          ? param.value
          : convertTermByOperator(value, (criteria as GSComparison).operator),
      };
    },
  });
  return correctedNode as GS;
}

export function convertTypeCompoColumnIr(col: ComponentState): ComponentState {
  // Mapping des typeCompo en typeCompo AG Grid
  switch (col.typeCompo) {
    case "DATE":
      if (col.options?.type === "datetime") {
        return {
          ...col,
          typeCompo: TypeSimpleComponent.CALENDAR_HOUR_SECOND,
          readOnly: ComponentReadOnly.READ_ONLY,
        };
      }
      if (col.options?.type === "datetimeHourMinute") {
        return {
          ...col,
          typeCompo: TypeSimpleComponent.CALENDAR_HOUR,
          readOnly: ComponentReadOnly.READ_ONLY,
        };
      }
      return {
        ...col,
        typeCompo: TypeSimpleComponent.CALENDAR,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "SYS_DOMAINE":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.SYS_DOMAINE_IR,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "BOOLEAN":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.BOOLEAN,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "BOOLEAN_ALT":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.CHECKBOX,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "NUMBER":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.INPUT_NUMBER,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "TAG":
      return { ...col, typeCompo: TypeSimpleComponent.TAG, readOnly: ComponentReadOnly.READ_ONLY };
    case "INPUT":
      switch (col.options?.type) {
        case "date":
          return {
            ...col,
            typeCompo: TypeSimpleComponent.CALENDAR,
            readOnly: ComponentReadOnly.DEFAULT,
            mandatory: true,
          };
        case "number":
          return {
            ...col,
            typeCompo: TypeSimpleComponent.INPUT_NUMBER,
            readOnly: ComponentReadOnly.DEFAULT,
            mandatory: true,
          };
        default:
          return {
            ...col,
            typeCompo: TypeSimpleComponent.INPUT,
            readOnly: ComponentReadOnly.DEFAULT,
            mandatory: true,
          };
      }
    //return { ...col, typeCompo: "I", readOnly: ComponentReadOnly.DEFAULT, mandatory: true };
    case "TEXT":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.INPUT,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "LINK":
      return { ...col, typeCompo: TypeSimpleComponent.LINK, readOnly: ComponentReadOnly.READ_ONLY };
    case "STATIC_HTML":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.STATIC_HTML,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    case "PROCESSUS_LINK":
      return {
        ...col,
        typeCompo: TypeSimpleComponent.PROCESSUS_LINK,
        readOnly: ComponentReadOnly.READ_ONLY,
      };
    default:
      return col;
  }
}

function mapTypeCompoToFilterType(typeCompo: TypeSimpleComponentValue) {
  switch (typeCompo) {
    case TypeSimpleComponent.CALENDAR:
    case TypeSimpleComponent.CALENDAR_HOUR:
    case TypeSimpleComponent.CALENDAR_HOUR_SECOND:
      return "date";
    case TypeSimpleComponent.INPUT_NUMBER:
      return "number";
    case TypeSimpleComponent.CHECKBOX:
    case TypeSimpleComponent.BOOLEAN:
    case TypeSimpleComponent.SYS_DOMAINE:
    case TypeSimpleComponent.SYS_DOMAINE_IR:
      return "set";
    default:
      return "text";
  }
}

/**
 * Async Fonction chargé de récupérer les données de la table via un appel au backend
 */
export async function fetchData(props: {
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  currentFocus?: DatatableFocusState;
  query: RSQLCriteria;
  startIndex: number;
  stopIndex: number;
  reset: boolean;
  cancelToken: CancelTokenSource;
  oelColumns: string[];
  filterBar: FilterBar | undefined;
  groupBy?: string;
  groupByFunctions?: GroupByFunctionParameter[];
}): Promise<PagedResource<Pojo>> {
  const {
    sjmoCode,
    tableName,
    ctrlkey,
    currentFocus,
    query,
    startIndex,
    stopIndex,
    reset,
    cancelToken,
    oelColumns,
    filterBar,
    groupBy,
    groupByFunctions,
  } = props;

  if (!currentFocus || reset) {
    return { data: [], meta: { totalRecords: 0, size: 0 } };
  }
  const size = Math.max(stopIndex - startIndex, MIN_BATCH_SIZE);

  try {
    if (groupBy) {
      const { data } = await api.findAllWithGroupBy(
        {
          sjmoCode,
          tableName,
          filter: query.build(),
          first: startIndex,
          size,
          includeJoinParent: true,
          includeOels: oelColumns,
          includeStyle: true,
          includeRowClassName: currentFocus?.cssClass,
          contextKey: ctrlkey,
          filterBar: filterBar || undefined,
          groupBy,
          groupByFunctions: groupByFunctions || [],
        },
        cancelToken,
      );

      return data;
    } else {
      const { data } = await api.findAll(
        {
          sjmoCode,
          tableName,
          filter: query.build(),
          first: startIndex,
          size,
          includeJoinParent: true,
          includeOels: oelColumns,
          includeStyle: true,
          includeRowClassName: currentFocus?.cssClass,
          contextKey: ctrlkey,
          filterBar: filterBar || undefined,
          groupBy,
        },
        cancelToken,
      );
      return data;
    }
  } catch (e) {
    toaster.notify({
      id: uuidv4(),
      group: NotificationGroup.DEFAULT,
      title: t("commun_erreur_recuperation_donnees"),
      priority: "NORMAL",
      intent: "DANGER",
      createdAt: new Date().toISOString(),
    });
    return { data: [], meta: { totalRecords: 0, size: 0 } };
  }
}

type GetFocus = (
  sjmoCode: string,
  tableName: string,
  ctrlKey: string,
) => AxiosPromise<FocusState[]>;

async function loadDatatableDefinitionFrom(
  getFocus: GetFocus,
  sjmoCode: string,
  tableName: string,
  ctrlKey: string,
): Promise<[DatatableFocusState[], ComponentState[]]> {
  try {
    const focusRes = await getFocus(sjmoCode, tableName, ctrlKey);

    let columns: ComponentState[] = [];
    if (focusRes.data.length > 0) {
      const columnRes = await api.getColumn(sjmoCode, focusRes.data[0].focusId);
      columns = columnRes.data;
    }

    const focuses = focusRes.data.map((focus) => ({
      ...focus,
      groupByColumns: (focus as any).breakRows ?? null,
      defaultOpenBreakRows: (focus as any).defaultOpenBreakRows ?? false,
    }));

    return [focuses as DatatableFocusState[], columns];
  } catch (e) {
    return [[], []];
  }
}

export function loadDatatableDefinition(
  sjmoCode: string,
  tableName: string,
  ctrlKey: string,
): Promise<[DatatableFocusState[], ComponentState[]]> {
  return loadDatatableDefinitionFrom(api.getFocus, sjmoCode, tableName, ctrlKey);
}

export function loadDatatableDefinitionModeAdmin(
  sjmoCode: string,
  tableName: string,
  ctrlKey: string,
): Promise<[DatatableFocusState[], ComponentState[]]> {
  return loadDatatableDefinitionFrom(getDatatableFocusModeAdmin, sjmoCode, tableName, ctrlKey);
}
